import { useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { useNotificationStore } from '../store/notificationStore';
import { notificationService } from '../services/notificationService';
import { useNavigate } from 'react-router-dom';
import { captureError } from '../lib/errorHandler';

/**
 * Hook to initialize push notifications on native platforms via
 * @capacitor-firebase/messaging. Handles permission request, token
 * registration, foreground/background listeners, and deep linking on
 * notification tap.
 *
 * Runs on iOS and Android — no-op on web.
 */
export function usePushNotifications() {
  const navigate = useNavigate();
  const { addNotification, fetchUnreadCount, setFcmToken } = useNotificationStore();
  const tokenRef = useRef<string | null>(null);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let cleanupFns: Array<() => void> = [];

    const init = async () => {
      try {
        // Dynamic import to avoid bundling native plugins in web builds
        const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');

        // 1. Request notification permission (required on Android 13+)
        const permResult = await FirebaseMessaging.requestPermissions();
        if (permResult.receive !== 'granted') {
          console.log('[Push] Notification permission denied');
          return;
        }

        // 2. Get FCM token and register with backend
        const { token } = await FirebaseMessaging.getToken();
        tokenRef.current = token;
        setFcmToken(token);
        console.log('[Push] FCM token obtained');

        await notificationService.registerDevice({
          fcmToken: token,
          platform: Capacitor.getPlatform(),
          deviceInfo: {
            platform: Capacitor.getPlatform(),
          },
        });
        console.log('[Push] Device registered with backend');

        // 3. Listen for token refresh
        const tokenRefreshListener = await FirebaseMessaging.addListener(
          'tokenReceived',
          async (event) => {
            console.log('[Push] Token refreshed');
            tokenRef.current = event.token;
            setFcmToken(event.token);
            await notificationService.registerDevice({
              fcmToken: event.token,
              platform: Capacitor.getPlatform(),
            });
          },
        );
        cleanupFns.push(() => tokenRefreshListener.remove());

        // 4. Foreground notification handler
        const foregroundListener = await FirebaseMessaging.addListener(
          'notificationReceived',
          (event) => {
            console.log('[Push] Foreground notification:', event.notification);
            // Refresh unread count when a push arrives while app is open
            fetchUnreadCount();
          },
        );
        cleanupFns.push(() => foregroundListener.remove());

        // 5. Notification tap handler (background + killed state)
        const actionListener = await FirebaseMessaging.addListener(
          'notificationActionPerformed',
          (event) => {
            console.log('[Push] Notification tapped:', event.notification);
            const deepLink = (event.notification?.data as Record<string, unknown>)?.deepLink;
            if (deepLink && typeof deepLink === 'string') {
              navigate(deepLink);
            }
            // Refresh unread count
            fetchUnreadCount();
          },
        );
        cleanupFns.push(() => actionListener.remove());

        // 6. Fetch initial unread count
        fetchUnreadCount();
      } catch (error) {
        // Skip Sentry noise for 401s — the axios interceptor already handles auth
        // failures (refresh + logout). A 401 here just means the token expired
        // while push init was in flight.
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 401) {
          console.warn('[Push] Init aborted — auth expired');
          return;
        }
        captureError(error, 'PushNotifications.init');
      }
    };

    init();

    return () => {
      cleanupFns.forEach((fn) => fn());
    };
  }, []);

  // Return the current token ref for logout cleanup
  return { tokenRef };
}
